import { Person } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Issue } from 'validata';
import { useFetchAllCache } from '../../hooks/use-cache.js';
import { useAuthenticated } from '../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../utils/types.js';
import { formikCheck, issuesToFormik } from '../../utils/validation.js';
import { EditView } from './edit.view.js';
import { ExistingPersonPage } from './existing-person.js';

export const EditPersonContent: FC<ExistingEntityViewProps<Person.View>> = ({
  data: person,
  id,
}) => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const cache = useFetchAllCache('person');
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik<Person.Patch>({
    initialValues: {
      email: person.email,
      firstName: person.firstName,
      lastName: person.lastName,
      phone: person.phone ?? '',
      roles: person.roles ?? [],
    },
    onSubmit: (values) => {
      return auth.axios
        .patch<{ updated: Person.View }>(
          `/api/v1/person/${encodeURIComponent(id)}`,
          values,
        )
        .then(() => {
          cache.invalidate();
          navigate(-1);
          enqueueSnackbar('Updated', { variant: 'success' });
          return undefined;
        })
        .catch(
          (
            error: AxiosError<{
              error?: { message: string };
              issues: Issue[];
            }>,
          ) => {
            // e.g. a person shared with another organization, whose name, email
            // and phone only they can change - a user-facing answer, not a fault
            if (error.response?.status === 409) {
              enqueueSnackbar(
                error.response.data.error?.message ?? 'Could not save changes',
                { variant: 'error' },
              );
              return;
            }
            console.log(error);
            Sentry.captureException(error);
            if (error.response && error.response.status === 400) {
              if ('issues' in error.response.data) {
                const issues = error.response.data.issues;
                formik.setErrors(issuesToFormik(issues));
                return;
              }
            }
            // TODO setData(error);
          },
        );
    },
    validate: (value) => formikCheck(Person.patchContract, value),
  });

  return <EditView cancel={() => navigate(-1)} formik={formik} />;
};

export const EditPersonPage: FC = () => (
  <ExistingPersonPage view={EditPersonContent} />
);
